<template>
  <div class="family-group-list">
    <p class="help-text">
      <template v-if="props.forUserProfile">These are the groups this user belongs to that are also visible to you.</template>
      <template v-else>Use groups to share trees and explore them with other family members.</template>
    </p>
    <div v-if="pendingInvitesRef.length > 0" class="invite-area">
      <h4>
        <span class="header-text">
          Invitations
          <span class="badge notify-badge rounded-pill">{{ pendingInvitesRef.length }}</span>
        </span>
      </h4>
      <ul class="list-unstyled invite-list">
        <li v-if="pendingInvitesRef.length == 0" class="empty-list">
          None
        </li>
        <li v-for="inv in pendingInvitesRef" :key="inv.id">
          <FamilyGroupInviteItem :inviteId="inv.id" select-mode @click="showInvite"/>
        </li>
      </ul>
      <h4 class="mt-3">Member of</h4>
    </div>
    <ul v-if="familyGroupsRef.length" class="list-unstyled group-list">
      <li v-for="fg in familyGroupsRef" :key="fg.id">
        <FamilyGroupItem :family-group-id="fg.id" :user-id="userIdRef"/>
      </li>
    </ul>
    <div v-else class="empty-list">
      <template v-if="isSelfRef">You are not a member of any groups.</template>
      <template v-else>This user is not a member of any groups.</template>
    </div>
    <div class="actions-row">
      <button type="button" v-if="canAddGroupRef" class="btn btn-primary" :class="{ ['btn-sm']: familyGroupsRef.length }" @click="createGroup">
        <PlusIcon size="1.2x" class="mb-1"/> Create a group
      </button>
    </div>
    <FamilyGroupInviteModal ref="inviteModalRef"/>
  </div>
</template>

<style lang="scss" scoped>
.invite-area {
  h4 .header-text {
    position: relative;

    .notify-badge {
      position: absolute;
      top: 0px;
      right: -16px;
      padding: 3px 6px;
      background-color: var(--bs-danger);
      font-size: 0.75rem;
    }
  }
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.empty-list {
  font-size: 0.875rem;
  color: #888;
}

.actions-row {
  margin-top: 1.5rem;
  font-size: 0.875rem;
}
</style>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { FamilyGroup, InvitationResult, PrincipalType } from '@/gp/GroupAdminModel'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useFamilyGroupInviteStore } from '@/gp/FamilyGroupInviteStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useUserStore } from '@/gp/UserStore'
import { LoadMode } from '@/util/AsyncData'
import { PlusIcon } from '@zhuowenli/vue-feather-icons'
import { useTokenManager } from '@/auth/TokenManager'
import { sortBy, uniq } from 'lodash'
import FamilyGroupInviteItem from './FamilyGroupInviteItem.vue'
import FamilyGroupItem from './FamilyGroupItem.vue'
import FamilyGroupInviteModal from './FamilyGroupInviteModal.vue'

const props = defineProps<{
  userId?: string
  forUserProfile?: boolean
}>()

const inviteModalRef = ref<InstanceType<typeof FamilyGroupInviteModal>>()

const userStore = useUserStore()
const familyGroupStore = useFamilyGroupStore()
const inviteStore = useFamilyGroupInviteStore()
const memberStore = useFamilyGroupMemberStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const tokenManager = useTokenManager()
const router = useRouter()

const userIdRef = computed(() => props.userId ?? tokenManager.current.userId)
const userRef = computed(() => userStore.getAsyncUser(userIdRef.value, LoadMode.EnsureLoaded)?.data)
const isSelfRef = computed(() => userIdRef.value == tokenManager.current.userId)
const pendingInvitesRef = computed(() => inviteStore.getInvitesForUser(userIdRef.value, LoadMode.EnsureLoaded)
  .filter(inv => inv.result == InvitationResult.Pending))
const familyGroupsRef = computed(() => {
  const fms = memberStore.getMembersForUsers([userIdRef.value], LoadMode.EnsureLoaded)
  const dms = dataGroupMemberStore.getMembersForDataGroups([userRef.value?.profileDataGroupId], LoadMode.EnsureLoaded)
    .filter(dm => dm.principalType == PrincipalType.FamilyGroup)
  const familyGroupIds = uniq([...fms.map(fm => fm.familyGroupId), ...dms.map(dm => dm.principalId)])
  const familyGroups = familyGroupStore.getGroupList(familyGroupIds) // loaded inline
  sortBy(familyGroups, fg => fg.name)
  return familyGroups;
})

const canAddGroupRef = computed(() => !props.forUserProfile && isSelfRef.value && !tokenManager.current.isSupervised)

async function showInvite(inviteId: string) {
  inviteModalRef.value?.openEdit(inviteId)
}

async function createGroup() {
  if (userRef.value) {
    const fg = new FamilyGroup()
    fg.name = FamilyGroup.getDefaultName(userRef.value)
    const fgId = await familyGroupStore.addAsync(fg)
    router.push(FamilyGroup.getPageUrl(fgId))
  }
}

function onJoinGroup() {
  alert("join!")
}
</script>
